<template>
  <div
    class="magazine-media-container"
    :class="{
      'magazine-media-container--info-graphics': isInfographics,
      'magazine-media-container--full-width': isFullWidthImage,
    }"
  >
    <div class="magazine-media-image" :class="{ last: last, square: isSquare }">
      <div class="image-info">
        <div v-if="titleLabel" class="image-info-title">
          <strong> {{ titleLabel }} </strong>
        </div>
        <div v-if="text" class="image-info-text" v-html="text">
          {{ text }}
        </div>
      </div>
      <div v-if="isVideo || imageSrc" class="magazine-media-image-container">
        <video v-if="isVideo" autoplay loop muted playsinline>
          <source :src="videoWebm" type="video/webm" />
          <source :src="videoMp4" type="video/mp4" />
          <p v-if="videoFallback">
            <a :href="videoWebm">{{ videoFallback }}</a>
          </p>
        </video>
        <img
          v-else-if="imageSrc"
          :id="imageid"
          ref="imgRef"
          :key="componentKey"
          :src="imageSrcData"
          :class="{ infografic_clickable: replayclick }"
          :srcset="imageSrcset"
          @click="imageclicked"
        />
      </div>
      <div v-if="videoSrc" class="magazine-media-iframe-container">
        <iframe
          width="960"
          height="540"
          :src="videoSrc"
          frameborder="0"
        ></iframe>
      </div>
      <div v-if="$slots.default" class="image-description">
        <slot></slot>
      </div>
    </div>
  </div>
</template>

<script>
import 'intersection-observer'

import inViewport from 'vue-in-viewport-mixin'

export default {
  name: 'SanitasMagazineMedia',

  mixins: [inViewport],

  props: {
    imageSrc: {
      type: String,
      default: '',
    },
    imageSrcset: {
      type: String,
      default: '',
    },
    videoWebm: {
      type: String,
      default: '',
    },
    videoMp4: {
      type: String,
      default: '',
    },
    videoFallback: {
      type: String,
      default: '',
    },
    isFullWidthImage: Boolean,
    isVideo: Boolean,
    isSquare: Boolean,
    isInfographics: Boolean,
    videoSrc: {
      type: String,
      default: '',
    },
    replayviewport: Boolean,
    replayclick: Boolean,
    imageid: {
      type: String,
      default: '',
    },
    titleLabel: {
      type: String,
      default: '',
    },
    text: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      last: false,
      componentKey: 0,
      imageSrcData: this.imageSrc,
      firsttime: true,
      onetimehiddenafteroneshow: false,
    }
  },

  computed: {
    imageSizes() {
      if (this.last) {
        return '(min-width: 1100px) 1100px, 100vw'
      } else {
        return '(min-width: 1100px) 540px, (min-width: 860px) 50vw, 100vw'
      }
    },
  },
  watch: {
    'inViewport.fully': function (visible) {
      if (this.replayviewport) {
        if (visible) {
          if (!this.firsttime && this.onetimehiddenafteroneshow) {
            this.reloadPic()
          }
          this.firsttime = false
        }
        if (!this.firsttime) {
          if (!visible) {
            this.onetimehiddenafteroneshow = true
          }
        }
      }
    },
  },
  created() {
    this.componentKey = Math.floor(Math.random() * 1000000)
  },

  mounted() {
    const element = this.$el

    // add data elements to enable automatic 50% width
    if (!element.dataset.magazineMediaCount) {
      element.dataset.magazineMediaCount = 1
      // fullwidth doesn't need magazineMediaParity attribute
      if (!this.isFullWidthImage) {
        element.dataset.magazineMediaParity = 'odd'
      }
    }
    if (
      element.nextElementSibling &&
      element.nextElementSibling.classList &&
      element.nextElementSibling.classList.contains(
        'magazine-media-container',
      ) &&
      !element.nextElementSibling.classList.contains(
        'magazine-media-container--full-width',
      ) &&
      !this.isFullWidthImage
    ) {
      element.dataset.magazineMediaLast = 'no'
      // set magazineMediaCount and parity for the next element
      element.nextElementSibling.dataset.magazineMediaCount =
        parseInt(element.dataset.magazineMediaCount) + 1
      element.nextElementSibling.dataset.magazineMediaParity =
        element.dataset.magazineMediaParity === 'odd' ? 'even' : 'odd'

      if (element.nextSibling && element.nextSibling.textContent) {
        element.nextSibling.textContent = element.nextSibling.textContent.trim()
      }
    } else {
      element.dataset.magazineMediaLast = 'yes'
      this.last = true
    }

    this.setEelementMarginLeft()

    if (this.isInfographics) {
      window.addEventListener('resize', this.setEelementMarginLeft)
    }
  },
  beforeDestroy() {
    if (this.isInfographics) {
      window.removeEventListener('resize', this.setEelementMarginLeft)
    }
  },

  methods: {
    imageclicked: function (event) {
      if (this.replayclick) {
        this.reloadPic()
      }
    },
    calculateMarginLeft() {
      const width =
        document.body.clientWidth ||
        document.documentElement.clientWidth ||
        window.innerWidth
      if (width > 800 && width < 1100) {
        return (width - 800) / 2
      } else if (width > 1100) {
        return 151
      } else {
        return 0
      }
    },

    reloadPic: function () {
      let srcorig = this.$refs.imgRef.src

      if (srcorig.indexOf('iecb=') != -1) {
        srcorig = srcorig.substring(0, srcorig.indexOf('iecb=') - 1)
      }

      this.imageSrcData = srcorig + '?iecb=' + Math.floor(Math.random() * 30)

      this.forceRerender()
    },

    forceRerender() {
      this.componentKey += 1
    },
    setEelementMarginLeft() {
      const element = this.$el
      if (
        this.isInfographics &&
        element.dataset.magazineMediaParity === 'odd'
      ) {
        element.style.marginLeft = this.calculateMarginLeft() + 'px'
      }
    },
  },
}
</script>

<style lang="postcss">
.magazine-article
  .content-area
  .magazine-media-container
  .magazine-media-image
  .image-info {
  .image-info-text p {
    font-size: 15px;
    margin-bottom: 20px;
  }
  .image-info-text p {
    /* overwrite .magazine-article .content-area p */
    font-family: 'Open Sans', sans-serif;
  }
}

.magazine-article
  .content-area
  .content-column
  > article
  .magazine-media-container--full-width {
  margin-left: 0;
  margin-right: 0;
  padding-left: 0;
  padding-right: 0;
  width: 100%;

  .image-info,
  .image-description {
    width: var(--magazine-article-content-width-default);
    margin-left: auto;
    margin-right: auto;
    padding-left: 0;
    padding-right: 0;
  }

  @media (max-width: 860px) {
    .image-info,
    .image-description {
      padding-left: 30px;
      padding-right: 30px;
      width: auto;
    }
    .image-description {
      margin-left: 0;
      margin-right: 0;
    }
    .image-info {
      margin-left: 10px;
      margin-right: 10px;
    }
  }

  @media (max-width: 490px) {
    .image-info,
    .image-description {
      padding-left: 0;
      padding-right: 0;
    }
    .image-description {
      margin-left: 10px;
      margin-right: 10px;
    }
  }
}

.magazine-media-container[data-magazine-media-count] {
  margin-top: 60px;
  margin-bottom: 60px;

  &[data-magazine-media-count][data-magazine-media-last='no'],
  &[data-magazine-media-last='yes'][data-magazine-media-parity='even'] {
    display: inline-block;
    vertical-align: top;
    width: 540px;
    margin-top: 20px;
  }

  &[data-magazine-media-count][data-magazine-media-last='no'] {
    margin-left: 0;
    margin-right: 0;
  }

  &[data-magazine-media-count][data-magazine-media-parity='even'] {
    margin-left: 20px;
    margin-right: -20px;
  }

  &[data-magazine-media-count][data-magazine-media-last='yes'][data-magazine-media-parity='odd'] {
    display: block;

    .image-description {
      padding-left: 0;
    }

    @media (min-width: 860px) {
      .image-info {
        padding-left: 0;
      }
    }
  }

  @media (max-width: 1100px) {
    &[data-magazine-media-count][data-magazine-media-last='no'],
    &[data-magazine-media-count][data-magazine-media-last='yes'][data-magazine-media-parity='even'] {
      width: calc(50% - 10px);
      padding-left: 0;
      padding-right: 0;
    }

    &[data-magazine-media-count][data-magazine-media-last='yes'][data-magazine-media-parity='odd'] {
      display: block;
      padding-left: 0;
      padding-right: 0;
    }
  }

  @media (max-width: 860px) {
    &[data-magazine-media-count][data-magazine-media-last='no'],
    &[data-magazine-media-count][data-magazine-media-last='yes'][data-magazine-media-parity='even'] {
      display: block;
      width: 100%;
      max-width: 600px;
      padding: 0 30px;
      margin-right: auto;
      margin-left: auto;

      .image-description {
        padding-left: 0;
      }

      &.magazine-media-container--info-graphics {
        margin-left: 0 !important;
      }
    }

    &[data-magazine-media-count][data-magazine-media-last='yes'][data-magazine-media-parity='odd'] {
      padding: 0 30px;
      margin-left: 0;
    }
  }

  @media (max-width: 490px) {
    &[data-magazine-media-count][data-magazine-media-last='no'],
    &[data-magazine-media-count][data-magazine-media-last='yes'][data-magazine-media-parity='even'] {
      padding: 0;

      &.magazine-media-container--info-graphics {
        padding-left: 10px;
        padding-right: 10px;
      }

      .image-description {
        padding-left: 10px;
        padding-right: 10px;
      }
    }

    &[data-magazine-media-count][data-magazine-media-last='yes'][data-magazine-media-parity='odd'] {
      padding: 0;

      &.magazine-media-container--info-graphics {
        padding-left: 10px;
        padding-right: 10px;
      }

      .image-description {
        padding-left: 10px;
        padding-right: 10px;
      }
    }
  }

  .magazine-media-image {
    width: 100%;
  }

  .image-info {
    padding: 0 10px 20px;
    font-size: 15px;
  }

  &.magazine-media-container--info-graphics {
    max-width: 390px !important;

    .image-info {
      padding: 0 0 20px;
    }
  }

  .magazine-media-iframe-container {
    position: relative;
    overflow: hidden;
    padding-top: 60%;

    iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border: 0;
    }
  }

  &[data-magazine-media-count][data-magazine-media-last='no'],
  &[data-magazine-media-count][data-magazine-media-last='yes'][data-magazine-media-parity='even'],
  &[data-magazine-media-count][data-magazine-media-last='yes'][data-magazine-media-parity='odd'] {
    .magazine-media-image-container {
      position: relative;
      overflow: hidden;
      padding-top: 60%;

      img,
      video {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .square > .magazine-media-image-container {
      padding-top: 100%;
    }
  }

  img,
  video {
    width: 100%;
    border: 1px solid var(--c-secondary-neutral-3);
  }

  .infografic_clickable {
    cursor: pointer;
  }

  .image-description {
    padding: 10px;
    line-height: 21px;
    font-size: 15px;
    color: var(--c-primary-neutral-1);

    @media (max-width: 1100px) {
      padding-top: 10px;
    }
    @media (max-width: 860px) {
      padding-left: 15px;
    }
    @media (max-width: 490px) {
      font-size: 12px;
      line-height: 15px;
    }
  }
}

.iconbox .magazine-media-container[data-magazine-media-count] {
  &[data-magazine-media-count][data-magazine-media-last='no'],
  &[data-magazine-media-count][data-magazine-media-last='yes'][data-magazine-media-parity='even'] {
    display: block;
    width: 100%;
    max-width: unset;
    padding: 0;
    margin-left: 0;

    .image-description {
      padding-left: 0;
    }
  }

  &[data-magazine-media-count][data-magazine-media-last='yes'][data-magazine-media-parity='odd'] {
    padding: 0;
  }
}
</style>
